/**
 * C-GTCC-VIEW-OVW1 — GTCC view "Operations Overview", 1:1 after
 * `GTCC_REFERENCE_2026-09-01.html`, `div[data-v="ovw"]` (lines 545-584).
 *
 * Layout of the reference, element for element:
 *   - head "Operations Overview" with the freshness badge and the `.sub`
 *     line "Antwortet in ~10 Sekunden: Ist die Plattform gesund?" (the
 *     reference titles the standalone page with `h1`; inside the deck shell
 *     that `h1` belongs to the page frame, so the view titles itself `h2`),
 *   - `.tiles`: CRITICAL ALERTS · ACTIVE INCIDENTS · INFRA & VPS ·
 *     DATA CONFIDENCE, each framed by its own verdict,
 *   - `.grid-main`, first column: "Kern-Metriken" and "Zuletzt passiert",
 *   - `.grid-main`, second column: "Customer Risk Radar (Kurzform)" and
 *     "Quick Actions".
 *
 * P17 of the program document: Operations Overview is the only view that may
 * aggregate. That licence is used exactly twice — for DATA CONFIDENCE (share
 * of fresh sources) and for the Risk Radar short form (highest-risk tenant,
 * the rest summarised).
 *
 * Honesty (Owner P8/P9/P10): every number comes from a payload. A source that
 * is pending, failed, stale or `null` renders "nicht messbar" and a grey dot —
 * never a zero, never the previous value, never a sample figure. The
 * reference's own numbers are sample data (`GTCC_REFERENCE_SPEC.md`: "Numbers
 * shown in the reference are sample data … and are never copied into the
 * product"), so only its structure, its texts and its status semantics are
 * reproduced here — not its figures and not the tile frames those figures
 * imply.
 *
 * ACTIVE INCIDENTS is derived: the repository has no incident module and the
 * GTCC contract carries no incident endpoint, so — as the instruction
 * prescribes for that case — the number is the count of firing alert families
 * with severity `critical` from `rules/summary`, and the status line says that
 * it is derived.
 */
import { ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, type CSSProperties, type ReactNode } from "react";

import {
  FLEET_SUMMARY_OVERVIEW_KEY,
  fetchFleetSummary,
  type FleetSummaryDto,
} from "../../api/fleet.js";
import { isFreshStatus, type GtccResult, type GtccStatus } from "../../api/gtcc-result.js";
import {
  OPS_BACKUP_READINESS_KEY,
  OPS_RECENT_CHANGES_KEY,
  OPS_VEHICLE_FLOW_KEY,
  fetchOpsBackupReadiness,
  fetchOpsRecentChanges,
  fetchOpsVehicleFlow,
  type OpsBackupReadinessDto,
  type OpsRecentChangesDto,
  type OpsVehicleFlowDto,
} from "../../api/ops.js";
import { RISK_TENANTS_OVERVIEW_KEY, fetchRiskRadar, type RiskRadarDto } from "../../api/risk.js";
import {
  RULES_SUMMARY_KEY,
  SVC_DELIVERY_WATCHDOG_KEY,
  SVC_SERVICES_KEY,
  fetchRulesSummary,
  fetchSvcDeliveryWatchdog,
  fetchSvcServices,
  type RulesSummaryDto,
  type SvcDeliveryWatchdogDto,
  type SvcServicesDto,
} from "../../api/svc.js";
import { writeGtccViewKeyToLocation } from "../../deep-link-url.js"; // Blatt-Modul: bricht den Zyklus registry -> OvwView -> deep-link -> registry.
import {
  Dot,
  formatFreshnessAge,
  FreshBadge,
  GridMain,
  Panel,
  Tile,
  TilesGrid,
} from "../../ui/primitives.js";
import {
  formatDecimal1,
  formatInteger,
  measuredNumber,
  formatPercent2,
  toneOfStatus,
  toneOfVerdict,
  type ControlTone,
} from "../control/ControlPrimitives.js";
import { compareByRiskDesc, computeRiskScore } from "../risk/risk-score.js";

/* -------------------------------------------------------- local adapters */

/*
 * `CockpitTone` (`ui/primitives.tsx`) and `ControlTone` (`ControlPrimitives.tsx`)
 * are the same five-value string union, so the shared components accept
 * `ControlTone` values directly wherever a `CockpitTone` is expected below.
 */

const HEAD_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-3)",
  flexWrap: "wrap",
};

/*
 * C-GTCC-SHELL-1ZU1 — Seitentitel = `h1` der Referenz
 * (GTCC_REFERENCE_2026-09-01.html:67): Sora 19 px/700 in `--tx`. Die Werte
 * kommen aus dem Token-Satz, damit jede Deck-View denselben Titel traegt;
 * vorher hat jede View ihre eigene Typo-Stufe gesetzt (15/19/20/24 px).
 */
const TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: "var(--gt-cmp-gtcc-view-title-family)",
  fontSize: "var(--gt-cmp-gtcc-view-title-size)",
  fontWeight: "var(--gt-cmp-gtcc-view-title-weight)",
  color: "var(--tx)",
};

const SUB_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text-muted)",
};

/*
 * `ControlHead` has no shared-kit equivalent (the reference's standalone `h1`
 * head does not exist as a component in `ui/primitives.tsx`): kept local.
 */
function ControlHead(props: {
  title: string;
  subtitle: string;
  fresh?: ReactNode;
  testId?: string;
}): ReactNode {
  return (
    <div data-testid={props.testId}>
      <div style={HEAD_STYLE}>
        <h2 style={TITLE_STYLE}>{props.title}</h2>
        {props.fresh}
      </div>
      <p style={SUB_STYLE}>{props.subtitle}</p>
    </div>
  );
}

/*
 * `FreshBadge` carries no `testId`/`data-stale` of its own; this file's tests
 * assert both directly (`OvwView.test.tsx`), so the call site wraps it —
 * same pattern as `LaunchDot` in `LaunchView.tsx`.
 */
function ControlFreshBadge(props: {
  text?: string;
  measuredAt?: string | Date | null;
  stale?: boolean;
  testId?: string;
}): ReactNode {
  const stale = props.stale === true;
  return (
    <span data-testid={props.testId} data-stale={stale ? "true" : "false"}>
      <FreshBadge label={props.text} measuredAt={props.measuredAt} stale={stale} />
    </span>
  );
}

/** `.mono` — the right-hand value of a panel row, a table cell, a note. */
function ControlMono(props: {
  children: ReactNode;
  tone?: ControlTone | undefined;
  testId?: string;
}): ReactNode {
  return (
    <span
      data-testid={props.testId}
      className="mono"
      style={props.tone === undefined ? undefined : { color: `var(--${props.tone})` }}
    >
      {props.children}
    </span>
  );
}

/** Grey "nicht messbar" — the only thing that ever stands in for a number. */
function ControlNotMeasurable(props: { label: string; testId?: string }): ReactNode {
  return (
    <span data-testid={props.testId} data-unmeasurable="true" className="mono muted">
      {props.label}
    </span>
  );
}

/*
 * `.row` reimplemented locally rather than wrapping the shared `Row`: this
 * file's tests read `data-testid`/`data-tone` straight off the `.row` div,
 * and `Row` exposes neither. Wrapping `Row` in an extra element would also
 * make every row "first of type" inside its own wrapper, losing the
 * `.row:first-of-type { border-top: none }` separator. `.row .mono` already
 * right-aligns the value (see `cockpit-ui.css`), so no extra style is needed
 * here for that.
 */
function ControlRow(props: {
  tone: ControlTone;
  label: ReactNode;
  value?: ReactNode;
  testId?: string;
}): ReactNode {
  return (
    <div className="row" data-testid={props.testId} data-tone={props.tone}>
      <Dot tone={props.tone} />
      <span>{props.label}</span>
      {props.value}
    </div>
  );
}

/**
 * The same row as an activator — the reference's Quick Actions are rows that
 * navigate ("→ Monitoring Cockpit öffnen"). A `button` rather than a link
 * because the target is a view of this single-page shell. No shared-kit
 * equivalent exists; the `.row` class is element-agnostic (verified in
 * `cockpit-ui.css`), so it applies to a `<button>` unchanged.
 */
function ControlActionRow(props: {
  tone: ControlTone;
  label: string;
  onActivate: () => void;
  testId?: string;
}): ReactNode {
  return (
    <button
      type="button"
      className="row"
      data-testid={props.testId}
      data-tone={props.tone}
      onClick={props.onActivate}
      style={{
        width: "100%",
        textAlign: "start",
        background: "none",
        border: "none",
        cursor: "pointer",
        font: "inherit",
      }}
    >
      <Dot tone={props.tone} />
      <span>{props.label}</span>
    </button>
  );
}

/*
 * `ControlTile` → shared `Tile`, wrapped for the `data-testid`/`data-tone`
 * attributes this file's tests read off the tile's own element and its
 * status line (`Tile` exposes neither on its own).
 */
function ControlTile(props: {
  label: string;
  value: ReactNode;
  suffix?: string | undefined;
  statusText: string;
  tone: ControlTone;
  statusTone: ControlTone;
  testId?: string;
}): ReactNode {
  return (
    <div data-testid={props.testId} data-tone={props.tone}>
      <Tile
        tone={props.tone}
        title={props.label}
        value={props.value}
        {...(props.suffix === undefined ? {} : { valueSuffix: props.suffix })}
        statusTone={props.statusTone}
        status={
          <span
            data-testid={props.testId === undefined ? undefined : `${props.testId}-status`}
            data-tone={props.statusTone}
          >
            {props.statusText}
          </span>
        }
      />
    </div>
  );
}

function ControlTiles(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <TilesGrid {...(props.testId === undefined ? {} : { testId: props.testId })}>
      {props.children}
    </TilesGrid>
  );
}

/*
 * `ControlPanel` keeps a local `<h3 className="panel">` construct rather than
 * adopting the shared `Panel` component: `Panel` always renders its own
 * `<h2>`, and this view already renders its single level-1/2 heading in
 * `ControlHead` above. A second `Panel`-owned `h2` per panel call site (five
 * panels here) would violate the "exactly one level-1/2 heading per view"
 * a11y rule (`gtcc-a11y-smoke.test.tsx`). Both `OvwView.test.tsx` and
 * `RiskView.test.tsx` accept `h2, h3` when locating a panel's own heading,
 * confirming `h3` is a supported shape. Trade-off: the panel box (background,
 * border, padding) comes from `cockpit-ui.css`'s `.panel` selector, but that
 * file defines heading typography only for `.panel h2`, not `.panel h3` — so
 * the heading's font-size/weight/color/gap are restated here from the same
 * values `PANEL_HEADING_STYLE` used before this migration, since there is no
 * CSS rule to fall back on for an `h3` in this position.
 */
function ControlPanel(props: { title: string; children: ReactNode; testId?: string }): ReactNode {
  return (
    <Panel title={props.title} headingLevel="h3" testId={props.testId}>
      {props.children}
    </Panel>
  );
}

/*
 * `GRID_MAIN_STYLE`/`GRID_COLUMN_STYLE` → shared `GridMain` for the outer
 * `.grid-main` element (drop-in), plus a local column-wrapper style: CSS only
 * grants `flex: 1 1 auto` to a `.panel` nested one level inside a plain `div`
 * child of `.grid-main` (see `.grid-main > div > .panel` in
 * `cockpit-ui.css`), it does not itself set `display: flex` on that wrapper
 * div — so the flex column layout is still supplied inline here, unchanged
 * from before the migration.
 */
const GRID_COLUMN_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--gt-sys-space-3)",
  minWidth: 0,
};

/* ---------------------------------------------------------------- queries */

/** Ticket floor of the control views: never refetch faster than 30 s. */
const OVW_STALE_MS = 30_000;
const OVW_REFETCH_MS = 60_000;

const OVW_QUERY_OPTIONS = {
  staleTime: OVW_STALE_MS,
  refetchInterval: OVW_REFETCH_MS,
  refetchOnWindowFocus: false,
} as const;

function dataOf<T>(query: UseQueryResult<GtccResult<T>>): T | null {
  return query.data !== undefined && query.data.ok ? query.data.data : null;
}

/* ------------------------------------------------------------- aggregation */

/**
 * One entry of the Data Confidence denominator. Only endpoints of the GTCC
 * convention take part: they are the ones that carry a `status`, and the share
 * is defined over `status != stale | unmeasurable`.
 */
interface ConfidenceSource {
  readonly settled: boolean;
  readonly fresh: boolean;
}

function confidenceOf<T extends { readonly status: GtccStatus }>(
  query: UseQueryResult<GtccResult<T>>,
): ConfidenceSource {
  if (query.isPending || query.data === undefined) return { settled: false, fresh: false };
  if (!query.data.ok) return { settled: true, fresh: false };
  return { settled: true, fresh: isFreshStatus(query.data.data.status) };
}

interface Confidence {
  /** 0…100, `null` while no source has settled. */
  readonly percent: number | null;
  readonly staleCount: number;
}

function aggregateConfidence(sources: ReadonlyArray<ConfidenceSource>): Confidence {
  const settled = sources.filter((source) => source.settled);
  if (settled.length === 0) return { percent: null, staleCount: 0 };
  const fresh = settled.filter((source) => source.fresh).length;
  return {
    percent: (100 * fresh) / settled.length,
    staleCount: settled.length - fresh,
  };
}

/**
 * The oldest measurement among the loaded sources — the head badge must not
 * claim the page is younger than its stalest number. `null` while nothing has
 * loaded.
 */
function oldestMeasuredAt(
  sources: ReadonlyArray<{ readonly measuredAt?: string } | null>,
): string | null {
  const timestamps = sources
    .map((source) => source?.measuredAt ?? null)
    .filter((value): value is string => value !== null && !Number.isNaN(new Date(value).getTime()));
  return timestamps.length === 0
    ? null
    : timestamps.reduce((oldest, value) => (new Date(value) < new Date(oldest) ? value : oldest));
}

/* ------------------------------------------------------------------- view */

export function OvwView(): ReactNode {
  const { t } = useLocale();

  const fleetSummaryQuery = useQuery({
    queryKey: FLEET_SUMMARY_OVERVIEW_KEY,
    queryFn: ({ signal }) => fetchFleetSummary(signal),
    ...OVW_QUERY_OPTIONS,
  });
  const vehicleFlowQuery = useQuery({
    queryKey: OPS_VEHICLE_FLOW_KEY,
    queryFn: ({ signal }) => fetchOpsVehicleFlow(signal),
    ...OVW_QUERY_OPTIONS,
  });
  const backupQuery = useQuery({
    queryKey: OPS_BACKUP_READINESS_KEY,
    queryFn: ({ signal }) => fetchOpsBackupReadiness(signal),
    ...OVW_QUERY_OPTIONS,
  });
  const recentChangesQuery = useQuery({
    queryKey: OPS_RECENT_CHANGES_KEY,
    queryFn: ({ signal }) => fetchOpsRecentChanges(signal),
    ...OVW_QUERY_OPTIONS,
  });
  const servicesQuery = useQuery({
    queryKey: SVC_SERVICES_KEY,
    queryFn: ({ signal }) => fetchSvcServices(signal),
    ...OVW_QUERY_OPTIONS,
  });
  const deliveryQuery = useQuery({
    queryKey: SVC_DELIVERY_WATCHDOG_KEY,
    queryFn: ({ signal }) => fetchSvcDeliveryWatchdog(signal),
    ...OVW_QUERY_OPTIONS,
  });
  const rulesQuery = useQuery({
    queryKey: RULES_SUMMARY_KEY,
    queryFn: ({ signal }) => fetchRulesSummary(signal),
    ...OVW_QUERY_OPTIONS,
  });
  const riskQuery = useQuery({
    queryKey: RISK_TENANTS_OVERVIEW_KEY,
    queryFn: ({ signal }) => fetchRiskRadar(signal),
    ...OVW_QUERY_OPTIONS,
  });

  const fleetSummary: FleetSummaryDto | null = dataOf(fleetSummaryQuery);
  const vehicleFlow: OpsVehicleFlowDto | null = dataOf(vehicleFlowQuery);
  const backup: OpsBackupReadinessDto | null = dataOf(backupQuery);
  const recentChanges: OpsRecentChangesDto | null = dataOf(recentChangesQuery);
  const services: SvcServicesDto | null = dataOf(servicesQuery);
  const delivery: SvcDeliveryWatchdogDto | null = dataOf(deliveryQuery);
  const rules: RulesSummaryDto | null = dataOf(rulesQuery);
  const risk: RiskRadarDto | null = dataOf(riskQuery);

  const queries = [
    fleetSummaryQuery,
    vehicleFlowQuery,
    backupQuery,
    recentChangesQuery,
    servicesQuery,
    deliveryQuery,
    rulesQuery,
    riskQuery,
  ];
  /* Nur `isPending`: eine Query, die mit einem Fehler gesettelt ist, traegt
     kein `data` — wer sie als „laedt noch" zaehlt, dreht den Spinner ewig. */
  const allPending = queries.every((query) => query.isPending);
  const anyLoaded =
    fleetSummary !== null ||
    vehicleFlow !== null ||
    backup !== null ||
    recentChanges !== null ||
    services !== null ||
    delivery !== null ||
    rules !== null ||
    risk !== null;

  /*
   * A 403 is not an unmeasured value. If nothing loaded and the refusal came
   * from the entitlement, the operator hears "not allowed" — never "could not
   * be measured" (Owner P9). The whole deck separates these two texts.
   */
  const deniedOnly = queries.some(
    (query) => query.data !== undefined && !query.data.ok && query.data.kind === "forbidden",
  );

  /*
   * P17 aggregation 1 of 2 — Data Confidence over the seven GTCC-convention
   * endpoints. `/v1/admin/ai/risk-radar` is deliberately not in the
   * denominator: it is the pre-existing module and its payload carries no
   * `status`, so counting it as fresh would be an assumption, not a
   * measurement.
   */
  const confidence = aggregateConfidence([
    confidenceOf(fleetSummaryQuery),
    confidenceOf(vehicleFlowQuery),
    confidenceOf(backupQuery),
    confidenceOf(recentChangesQuery),
    confidenceOf(servicesQuery),
    confidenceOf(deliveryQuery),
    confidenceOf(rulesQuery),
  ]);

  const oldestMeasurement = oldestMeasuredAt([
    fleetSummary,
    vehicleFlow,
    recentChanges,
    services,
    delivery,
    rules,
  ]);
  const oldestMeasurementSeconds =
    oldestMeasurement === null
      ? null
      : Math.max(0, Math.floor((Date.now() - new Date(oldestMeasurement).getTime()) / 1_000));

  const goTo = useCallback((key: string, search?: Record<string, string>) => {
    writeGtccViewKeyToLocation(key, false, import.meta.env.BASE_URL);
    if (search !== undefined && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      for (const [param, value] of Object.entries(search)) url.searchParams.set(param, value);
      window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    }
    if (typeof window !== "undefined") window.dispatchEvent(new PopStateEvent("popstate"));
  }, []);

  const head = (
    <ControlHead
      testId="gtcc-ovw-head"
      title={t("admin.gtcc.ovw.head.title")}
      subtitle={t("admin.gtcc.ovw.head.subtitle")}
      fresh={
        <ControlFreshBadge
          testId="gtcc-ovw-fresh"
          stale={confidence.staleCount > 0}
          text={
            oldestMeasurementSeconds === null
              ? t("admin.gtcc.ovw.fresh.unmeasurable")
              : t("admin.gtcc.fresh.age", {
                  age: formatFreshnessAge(oldestMeasurementSeconds),
                })
          }
        />
      }
    />
  );

  if (allPending) {
    return (
      <Stack gap={3} data-testid="gtcc-view-ovw">
        {head}
        <div data-testid="gtcc-ovw-loading">
          <LoadingState label={t("admin.gtcc.ovw.loading")} />
        </div>
      </Stack>
    );
  }

  if (!anyLoaded && deniedOnly) {
    return (
      <Stack gap={3} data-testid="gtcc-view-ovw">
        {head}
        <div data-testid="gtcc-ovw-failure">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.ovw.forbidden.title")}
            body={t("admin.gtcc.ovw.forbidden.body")}
          />
        </div>
      </Stack>
    );
  }

  if (!anyLoaded) {
    return (
      <Stack gap={3} data-testid="gtcc-view-ovw">
        {head}
        <div data-testid="gtcc-ovw-failure">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.ovw.failure.title")}
            body={t("admin.gtcc.ovw.failure.body")}
          />
        </div>
      </Stack>
    );
  }

  return (
    <Stack gap={3} data-testid="gtcc-view-ovw">
      {head}
      <ControlTiles testId="gtcc-ovw-tiles">
        <CriticalAlertsTile rules={rules} />
        <ActiveIncidentsTile rules={rules} />
        <InfraTile summary={fleetSummary} />
        <ConfidenceTile confidence={confidence} />
      </ControlTiles>

      <GridMain>
        <div style={GRID_COLUMN_STYLE}>
          <CoreMetricsPanel
            summary={fleetSummary}
            backup={backup}
            services={services}
            delivery={delivery}
            vehicleFlow={vehicleFlow}
          />
          <RecentChangesPanel changes={recentChanges} />
        </div>
        <div style={GRID_COLUMN_STYLE}>
          <RiskShortPanel vehicleFlow={vehicleFlow} />
          <ControlPanel testId="gtcc-ovw-quick-actions" title={t("admin.gtcc.ovw.qa.title")}>
            <Stack gap={0}>
              <ControlActionRow
                testId="gtcc-ovw-qa-cockpit"
                tone="info"
                label={t("admin.gtcc.ovw.qa.cockpit")}
                onActivate={() => goTo("cockpit")}
              />
              <ControlActionRow
                testId="gtcc-ovw-qa-ack"
                tone="info"
                label={t("admin.gtcc.ovw.qa.ack")}
                onActivate={() => goTo("alin")}
              />
              <ControlActionRow
                testId="gtcc-ovw-qa-audit"
                tone="info"
                label={t("admin.gtcc.ovw.qa.audit")}
                onActivate={() => goTo("audit", { period: "1d" })}
              />
            </Stack>
          </ControlPanel>
        </div>
      </GridMain>
    </Stack>
  );
}

/* ------------------------------------------------------------------ tiles */

function CriticalAlertsTile(props: { rules: RulesSummaryDto | null }): ReactNode {
  const { t, locale } = useLocale();
  const { rules } = props;
  const count = measuredNumber(rules?.firingCritical);

  /* The family the status line names: the crit family that has been firing
     longest, i.e. the one the Owner is asked about first. */
  const families = (Array.isArray(rules?.families) ? rules.families : []).filter(
    (family) => family.severity === "critical" && family.firing > 0,
  );
  const leading = [...families].sort(
    (a, b) =>
      (measuredNumber(b.firingSinceMinutes) ?? -1) - (measuredNumber(a.firingSinceMinutes) ?? -1),
  )[0];
  const leadingSince = leading === undefined ? null : measuredNumber(leading.firingSinceMinutes);

  const statusText =
    count === null
      ? t("admin.gtcc.ovw.not_measurable")
      : count === 0 || leading === undefined
        ? t("admin.gtcc.ovw.tile.critical.none")
        : leadingSince === null
          ? t("admin.gtcc.ovw.tile.critical.no_start", { family: leading.label })
          : t("admin.gtcc.ovw.tile.critical.since", {
              family: leading.label,
              minutes: formatInteger(leadingSince, locale),
            });

  const tone: ControlTone = count === null ? "muted" : count > 0 ? "crit" : "ok";
  return (
    <ControlTile
      testId="gtcc-ovw-tile-critical"
      label={t("admin.gtcc.ovw.tile.critical.label")}
      value={
        count === null ? (
          <ControlNotMeasurable label={t("admin.gtcc.ovw.not_measurable")} />
        ) : (
          formatInteger(count, locale)
        )
      }
      statusText={statusText}
      tone={tone}
      statusTone={tone}
    />
  );
}

function ActiveIncidentsTile(props: { rules: RulesSummaryDto | null }): ReactNode {
  const { t, locale } = useLocale();
  const { rules } = props;

  /* Derived, because the repository has no incident source: one incident per
     firing alert family of severity `critical`. A payload that arrives without
     the `families` array has not measured the families, so the tile says
     "nicht messbar" instead of reporting a zero it did not count. */
  const derived = Array.isArray(rules?.families)
    ? rules.families.filter((family) => family.severity === "critical" && family.firing > 0).length
    : null;

  const tone: ControlTone = derived === null ? "muted" : derived > 0 ? "crit" : "ok";
  return (
    <ControlTile
      testId="gtcc-ovw-tile-incidents"
      label={t("admin.gtcc.ovw.tile.incidents.label")}
      value={
        derived === null ? (
          <ControlNotMeasurable label={t("admin.gtcc.ovw.not_measurable")} />
        ) : (
          formatInteger(derived, locale)
        )
      }
      statusText={
        derived === null
          ? t("admin.gtcc.ovw.not_measurable")
          : derived === 0
            ? t("admin.gtcc.ovw.tile.incidents.none")
            : t("admin.gtcc.ovw.tile.incidents.derived", {
                count: formatInteger(derived, locale),
              })
      }
      tone={tone}
      statusTone={tone}
    />
  );
}

function InfraTile(props: { summary: FleetSummaryDto | null }): ReactNode {
  const { t, locale } = useLocale();
  const { summary } = props;
  const warnings = Array.isArray(summary?.warnings) ? summary.warnings : [];
  const healthy = measuredNumber(summary?.hostsHealthy);
  const total = measuredNumber(summary?.hostsTotal);
  const statusTone: ControlTone = summary === null ? "muted" : warnings.length > 0 ? "warn" : "ok";
  return (
    <ControlTile
      testId="gtcc-ovw-tile-infra"
      label={t("admin.gtcc.ovw.tile.infra.label")}
      value={
        healthy === null ? (
          <ControlNotMeasurable label={t("admin.gtcc.ovw.not_measurable")} />
        ) : (
          formatInteger(healthy, locale)
        )
      }
      suffix={
        total === null
          ? undefined
          : t("admin.gtcc.ovw.tile.infra.hosts", {
              total: formatInteger(total, locale),
            })
      }
      statusText={
        summary === null
          ? t("admin.gtcc.ovw.not_measurable")
          : warnings.length === 0
            ? t("admin.gtcc.ovw.tile.infra.no_warnings")
            : t("admin.gtcc.ovw.tile.infra.warnings", {
                count: formatInteger(warnings.length, locale),
                names: warnings.join(", "),
              })
      }
      tone={summary === null ? "muted" : toneOfStatus(summary.status)}
      statusTone={statusTone}
    />
  );
}

function ConfidenceTile(props: { confidence: Confidence }): ReactNode {
  const { t, locale } = useLocale();
  const { percent, staleCount } = props.confidence;
  const tone: ControlTone = percent === null ? "muted" : staleCount > 0 ? "warn" : "ok";
  return (
    <ControlTile
      testId="gtcc-ovw-tile-confidence"
      label={t("admin.gtcc.ovw.tile.confidence.label")}
      value={
        percent === null ? (
          <ControlNotMeasurable label={t("admin.gtcc.ovw.not_measurable")} />
        ) : (
          formatDecimal1(percent, locale)
        )
      }
      suffix={percent === null ? undefined : t("admin.gtcc.ovw.tile.confidence.unit")}
      statusText={
        percent === null
          ? t("admin.gtcc.ovw.tile.confidence.unmeasurable")
          : staleCount === 0
            ? t("admin.gtcc.ovw.tile.confidence.all_fresh")
            : t("admin.gtcc.ovw.tile.confidence.stale", {
                count: formatInteger(staleCount, locale),
              })
      }
      tone={tone}
      statusTone={tone}
    />
  );
}

/* ---------------------------------------------------------- core metrics */

function CoreMetricsPanel(props: {
  summary: FleetSummaryDto | null;
  backup: OpsBackupReadinessDto | null;
  services: SvcServicesDto | null;
  delivery: SvcDeliveryWatchdogDto | null;
  vehicleFlow: OpsVehicleFlowDto | null;
}): ReactNode {
  const { t, locale } = useLocale();
  const { summary, backup, services, delivery, vehicleFlow } = props;

  const uptime = measuredNumber(summary?.uptimePercent30d);
  const notMeasurable = <ControlNotMeasurable label={t("admin.gtcc.ovw.not_measurable")} />;

  const servicesRunning = measuredNumber(services?.running);
  const servicesTotal = measuredNumber(services?.total);
  const servicesMeasured = servicesRunning !== null && servicesTotal !== null;
  const vehiclesSending = measuredNumber(vehicleFlow?.sendingNow);
  const vehiclesTotal = measuredNumber(vehicleFlow?.vehiclesTotal);
  const vehiclesMeasured = vehiclesSending !== null && vehiclesTotal !== null;

  /* A payload without both channel objects has not measured delivery; the row
     then says "nicht messbar" rather than printing half a verdict. */
  const mail = delivery?.mail ?? null;
  const telegram = delivery?.telegram ?? null;
  const deliveryMeasured = mail !== null && telegram !== null;

  /* The worse of the two delivery channels decides the row's dot: a healthy
     mail queue never paints over a broken Telegram bot. */
  const deliveryTone: ControlTone = deliveryMeasured
    ? worstTone(toneOfStatus(mail.status), toneOfStatus(telegram.status))
    : "muted";

  /* Same rule for the backup row: both texts arrive together or not at all. */
  const backupMeasured =
    backup !== null &&
    typeof backup.classesText === "string" &&
    typeof backup.offsiteText === "string";

  return (
    <ControlPanel testId="gtcc-ovw-metrics" title={t("admin.gtcc.ovw.metrics.title")}>
      <Stack gap={0}>
        <ControlRow
          testId="gtcc-ovw-metrics-uptime"
          tone={uptime === null ? "muted" : "ok"}
          label={t("admin.gtcc.ovw.metrics.uptime")}
          value={
            uptime === null ? (
              notMeasurable
            ) : (
              <ControlMono>{formatPercent2(uptime, locale)}</ControlMono>
            )
          }
        />
        <ControlRow
          testId="gtcc-ovw-metrics-backup"
          tone={backupMeasured ? toneOfVerdict(backup.verdict) : "muted"}
          label={t("admin.gtcc.ovw.metrics.backup")}
          value={
            !backupMeasured ? (
              notMeasurable
            ) : (
              <ControlMono>
                {t("admin.gtcc.ovw.metrics.backup_value", {
                  classes: backup.classesText,
                  offsite: backup.offsiteText,
                })}
              </ControlMono>
            )
          }
        />
        <ControlRow
          testId="gtcc-ovw-metrics-services"
          tone={services === null ? "muted" : toneOfStatus(services.status)}
          label={t("admin.gtcc.ovw.metrics.services")}
          value={
            !servicesMeasured ? (
              notMeasurable
            ) : (
              <ControlMono>
                {t("admin.gtcc.ovw.metrics.services_value", {
                  running: formatInteger(servicesRunning, locale),
                  total: formatInteger(servicesTotal, locale),
                })}
              </ControlMono>
            )
          }
        />
        <ControlRow
          testId="gtcc-ovw-metrics-delivery"
          tone={deliveryTone}
          label={t("admin.gtcc.ovw.metrics.delivery")}
          value={
            !deliveryMeasured ? (
              notMeasurable
            ) : (
              <ControlMono>
                {t("admin.gtcc.ovw.metrics.delivery_value", {
                  mail: mail.label,
                  telegram: telegram.label,
                })}
              </ControlMono>
            )
          }
        />
        <ControlRow
          testId="gtcc-ovw-metrics-vehicles"
          tone={vehicleFlow === null ? "muted" : toneOfStatus(vehicleFlow.status)}
          label={t("admin.gtcc.ovw.metrics.vehicles")}
          value={
            !vehiclesMeasured ? (
              notMeasurable
            ) : (
              <ControlMono>
                {t("admin.gtcc.ovw.metrics.vehicles_value", {
                  sending: formatInteger(vehiclesSending, locale),
                  total: formatInteger(vehiclesTotal, locale),
                })}
              </ControlMono>
            )
          }
        />
      </Stack>
    </ControlPanel>
  );
}

const TONE_RANK: Readonly<Record<ControlTone, number>> = {
  ok: 0,
  info: 1,
  muted: 2,
  warn: 3,
  crit: 4,
};

function worstTone(a: ControlTone, b: ControlTone): ControlTone {
  return TONE_RANK[a] >= TONE_RANK[b] ? a : b;
}

/* -------------------------------------------------------- recent changes */

function RecentChangesPanel(props: { changes: OpsRecentChangesDto | null }): ReactNode {
  const { t } = useLocale();
  const { changes } = props;
  /* `null` = the endpoint did not answer or answered without the array; an
     empty array = it answered and there was nothing to report. The two say
     different things, so they keep different texts. */
  const rows = Array.isArray(changes?.changes) ? changes.changes : null;
  return (
    <ControlPanel testId="gtcc-ovw-recent" title={t("admin.gtcc.ovw.recent.title")}>
      {rows === null || rows.length === 0 ? (
        <ControlNotMeasurable
          testId="gtcc-ovw-recent-empty"
          label={
            rows === null ? t("admin.gtcc.ovw.not_measurable") : t("admin.gtcc.ovw.recent.empty")
          }
        />
      ) : (
        <Stack gap={0}>
          {rows.map((change) => (
            <ControlRow
              key={change.id}
              testId={`gtcc-ovw-recent-${change.kind}`}
              tone={toneOfVerdict(change.verdict)}
              label={change.text}
              value={<ControlMono>{change.relativeText}</ControlMono>}
            />
          ))}
        </Stack>
      )}
    </ControlPanel>
  );
}

/* ------------------------------------------------------- risk short form */

function RiskShortPanel(props: { vehicleFlow: OpsVehicleFlowDto | null }): ReactNode {
  const { t, locale } = useLocale();
  const tenants = Array.isArray(props.vehicleFlow?.tenants) ? props.vehicleFlow.tenants : [];

  /*
   * P17 aggregation 2 of 2 — the short form: the tenant with the highest risk
   * as its own warn row, every other tenant summarised in one line. The full
   * table lives in the Risk Radar view.
   */
  const scored = tenants
    .map((tenant) => ({
      name: tenant.name,
      silent: measuredNumber(tenant.silentOver24h),
      ...computeRiskScore(tenant),
    }))
    .sort(compareByRiskDesc);

  const leader = scored[0];
  const rest = scored.slice(1);

  if (leader === undefined) {
    return (
      <ControlPanel testId="gtcc-ovw-risk" title={t("admin.gtcc.ovw.radar.title")}>
        <ControlNotMeasurable
          testId="gtcc-ovw-risk-empty"
          label={
            props.vehicleFlow === null
              ? t("admin.gtcc.ovw.not_measurable")
              : t("admin.gtcc.ovw.radar.empty")
          }
        />
      </ControlPanel>
    );
  }

  const leaderTone: ControlTone =
    leader.band === "elevated" || leader.band === "watch"
      ? "warn"
      : leader.band === "low"
        ? "ok"
        : "muted";

  return (
    <ControlPanel testId="gtcc-ovw-risk" title={t("admin.gtcc.ovw.radar.title")}>
      <Stack gap={0}>
        <ControlRow
          testId="gtcc-ovw-risk-leader"
          tone={leaderTone}
          label={leader.name}
          value={
            leader.silent === null ? (
              <ControlNotMeasurable label={t("admin.gtcc.ovw.not_measurable")} />
            ) : (
              <ControlMono>
                {t("admin.gtcc.ovw.radar.silent_value", {
                  count: formatInteger(leader.silent, locale),
                })}
              </ControlMono>
            )
          }
        />
        {rest.length === 0 ? null : (
          <ControlRow
            testId="gtcc-ovw-risk-rest"
            tone="ok"
            label={rest.map((tenant) => tenant.name).join(" · ")}
            value={<ControlMono>{t("admin.gtcc.ovw.radar.rest_value")}</ControlMono>}
          />
        )}
      </Stack>
    </ControlPanel>
  );
}
